// The review-page voicemail player — the port of live's Wimpy widget
// (guitarreviews.php:118-131 + javascripts/scripts.js). One SHARED playlist of
// customer voicemails for the whole page: clicking any review tile's play
// button plays the NEXT clip in a runtime-shuffled order (live builds the
// player with random:1 and every tile's handler calls player.next()), and the
// only per-review touch is the big initial-letter backdrop taken from the
// clicked reviewer's name. Deviation from live, recorded in the ADR: no
// autoplay on load — browsers block audible autoplay anyway.
//
// The track list arrives either inline (<script data-vm-playlist> JSON) or by
// URL (data-manifest-url), whichever the migration manifest provides. No
// tracks = the component stays inert; the tile buttons only render when the
// manifest metaobject exists, so a missing manifest shows nothing at all.

class ZagerVoicemailPlayer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.tracks = [];
    this.order = [];
    this.pos = -1;
    this.currentIndex = -1;
    this.activeTile = null;

    this.letters = this.#json('[data-vm-letters]') ?? {};
    this.titleEl = this.querySelector('[data-vm-title]');
    this.listBox = this.querySelector('[data-vm-list]');
    this.seek = this.querySelector('[data-vm-seek]');
    this.letterImg = this.querySelector('[data-vm-letter]');
    this.playBtn = this.querySelector('[data-vm-play]');

    this.audio = new Audio();
    this.audio.preload = 'none';
    this.audio.addEventListener('ended', () => this.step(1));
    this.audio.addEventListener('timeupdate', () => this.#syncSeek());
    this.audio.addEventListener('play', () => this.#syncPlayState());
    this.audio.addEventListener('pause', () => this.#syncPlayState());

    this.querySelector('[data-vm-close]')?.addEventListener('click', () => this.#hide());
    this.querySelector('[data-vm-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-vm-next]')?.addEventListener('click', () => this.step(1));
    this.playBtn?.addEventListener('click', () => this.#toggle());
    this.querySelector('[data-vm-mute]')?.addEventListener('click', (event) => {
      this.audio.muted = !this.audio.muted;
      event.currentTarget.classList.toggle('is-muted', this.audio.muted);
    });
    this.seek?.addEventListener('input', () => {
      if (this.audio.duration) {
        this.audio.currentTime = (this.seek.value / 100) * this.audio.duration;
      }
    });

    document.addEventListener('click', (event) => {
      const tile = event.target.closest('.zager-review__vm');
      if (tile) this.#tileClick(tile);
    });

    const inline = this.#json('[data-vm-playlist]');
    if (inline) {
      this.#setTracks(inline);
    } else if (this.dataset.manifestUrl) {
      fetch(this.dataset.manifestUrl)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => json && this.#setTracks(json))
        .catch(() => {});
    }
  }

  #json(selector) {
    try {
      const el = this.querySelector(selector);
      return el ? JSON.parse(el.textContent) : null;
    } catch {
      return null;
    }
  }

  #setTracks(tracks) {
    if (!Array.isArray(tracks) || tracks.length === 0) return;
    this.tracks = tracks.filter((t) => t && t.url);
    // Fisher–Yates: the play order is shuffled once per page view, as live's
    // random:1 did. The visible list stays in canonical order.
    this.order = this.tracks.map((_, i) => i);
    for (let i = this.order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.order[i], this.order[j]] = [this.order[j], this.order[i]];
    }
    if (this.listBox) {
      this.listBox.replaceChildren(
        ...this.tracks.map((track, index) => {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'zager-vm__item';
          row.textContent = track.title ?? '';
          row.addEventListener('click', () => this.#playIndex(index));
          return row;
        }),
      );
    }
  }

  #tileClick(tile) {
    if (this.tracks.length === 0) return;
    this.#show();
    if (this.activeTile === tile && !this.audio.paused) {
      this.audio.pause();
      return;
    }
    this.#setActiveTile(tile);
    const letter = (tile.dataset.letter ?? '').toLowerCase();
    if (this.letterImg) {
      if (this.letters[letter]) {
        this.letterImg.src = this.letters[letter];
        this.letterImg.hidden = false;
      } else {
        this.letterImg.hidden = true;
      }
    }
    this.step(1);
  }

  #setActiveTile(tile) {
    this.activeTile?.classList.remove('is-playing');
    this.activeTile = tile;
    tile?.classList.add('is-playing');
  }

  step(delta) {
    if (this.tracks.length === 0) return;
    this.pos = (this.pos + delta + this.order.length) % this.order.length;
    this.#playIndex(this.order[this.pos], true);
  }

  #playIndex(index, fromOrder = false) {
    const track = this.tracks[index];
    if (!track) return;
    if (!fromOrder) this.pos = this.order.indexOf(index);
    this.currentIndex = index;
    this.audio.src = track.url;
    this.audio.play().catch(() => {});
    if (this.titleEl) this.titleEl.textContent = track.title ?? '';
    this.listBox?.querySelectorAll('.zager-vm__item').forEach((el, i) => {
      el.classList.toggle('is-current', i === index);
    });
    this.#show();
  }

  #toggle() {
    if (this.audio.paused) {
      if (this.currentIndex < 0) this.step(1);
      else this.audio.play().catch(() => {});
    } else {
      this.audio.pause();
    }
  }

  #syncPlayState() {
    const playing = !this.audio.paused;
    this.playBtn?.classList.toggle('is-playing', playing);
    this.activeTile?.classList.toggle('is-playing', playing);
  }

  #syncSeek() {
    if (this.seek && this.audio.duration) {
      this.seek.value = String((this.audio.currentTime / this.audio.duration) * 100);
    }
  }

  #show() {
    this.hidden = false;
  }

  #hide() {
    this.audio.pause();
    this.hidden = true;
    this.activeTile?.classList.remove('is-playing');
  }
}

if (!customElements.get('zager-voicemail-player')) {
  customElements.define('zager-voicemail-player', ZagerVoicemailPlayer);
}
